import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, Sparkles, Check, ShieldCheck, Loader2, Play } from 'lucide-react';
// Pre-computed particle vectors for the success-state confetti burst, so the
// pieces fan out evenly in all directions from the checkmark.
const CONFETTI_COLORS = ['#EA580C', '#F59E0B', '#22C55E', '#2B211A', '#FB923C'];
const CONFETTI = Array.from({
  length: 12
}, (_, i) => {
  const angle = i / 12 * Math.PI * 2;
  const dist = 26 + i % 3 * 6;
  return {
    x: Math.cos(angle) * dist,
    y: Math.sin(angle) * dist,
    r: (i % 2 === 0 ? 1 : -1) * (90 + i % 4 * 30),
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]
  };
});
type VideoKind = 'youtube' | 'vimeo' | 'loom' | 'file' | null;
// Detect how to embed whatever URL Tony drops in, so we render the right
// player without him having to think about it.
function getVideoEmbed(url?: string): {
  kind: VideoKind;
  src: string;
} | null {
  if (!url) return null;
  const u = url.trim();
  if (!u) return null;
  // YouTube (watch, youtu.be, or already-embed)
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return {
    kind: 'youtube',
    src: `https://www.youtube.com/embed/${yt[1]}`
  };
  // Loom
  const loom = u.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) return {
    kind: 'loom',
    src: `https://www.loom.com/embed/${loom[1]}`
  };
  // Vimeo
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return {
    kind: 'vimeo',
    src: `https://player.vimeo.com/video/${vimeo[1]}`
  };
  // Direct video file
  if (/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(u)) return {
    kind: 'file',
    src: u
  };
  // Fallback: assume it's already an embeddable URL
  return {
    kind: 'youtube',
    src: u
  };
}
export function WaitlistModal({
  onClose,
  videoUrl
}: {
  onClose: () => void;
  videoUrl?: string;
}) {
  const reduceMotion = useReducedMotion();
  const [email, setEmail] = useState('');
  const [keepPosted, setKeepPosted] = useState(true);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const embed = getVideoEmbed(videoUrl);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (status !== 'idle') return;
    setStatus('loading');
    window.setTimeout(() => setStatus('done'), 700);
  };
  return <motion.div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.12,
    ease: 'easeOut'
  }} data-id="element-3010">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#1A1410]/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-3011" />

      <motion.div role="dialog" aria-modal="true" aria-label="Join the Giga waitlist" className="relative w-full max-w-2xl max-h-[90dvh] overflow-auto overscroll-contain rounded-3xl bg-[#FAF8F4] text-[#2B211A] shadow-2xl shadow-black/40" initial={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.98,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.98,
      y: 8
    }} transition={reduceMotion ? {
      duration: 0.12
    } : {
      type: 'spring',
      stiffness: 420,
      damping: 32,
      mass: 0.7
    }} data-id="element-3012">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 text-[#2B211A] transition-colors" data-id="element-3013">
          <X size={18} data-id="element-3014" />
        </button>

        <div className="p-5 sm:p-7 md:p-9" data-id="element-3015">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase text-[#EA580C] mb-3 [letter-spacing:5px]" data-id="element-3016">
            <Sparkles size={13} data-id="element-3017" />
            Early access
          </div>

          {/* Heading — extra right padding on mobile so the long title never
            tucks under the close button. */}
          <h2 className="text-2xl md:text-3xl font-title font-bold tracking-tight leading-tight mb-2 pr-8 sm:pr-0" data-id="element-3018">
            Giga is invite-only — for now.
          </h2>
          <p className="text-sm md:text-base text-[#6B5D52] leading-relaxed max-w-xl mb-5 md:mb-6" data-id="element-3019">
            We're letting teams in a few at a time. Watch the quick walkthrough,
            then grab your spot on the waitlist — we'll email you the moment
            you're in.
          </p>

          {/* Tony: drop the product walkthrough here — pass videoUrl
           (YouTube/Loom/Vimeo/.mp4) to <WaitlistModal videoUrl="..." /> */}
          <div className="aspect-video w-full rounded-2xl overflow-hidden border border-[#E7E0D7] bg-[#2B211A] mb-6 md:mb-7" data-id="element-3020">
            {embed ? embed.kind === 'file' ? <video src={embed.src} controls playsInline className="w-full h-full object-cover" data-id="element-3021" /> : <iframe src={embed.src} title="Giga product walkthrough" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3022" /> : <div className="relative w-full h-full flex flex-col items-center justify-center gap-4" data-id="element-3023">
                {/* Faint dotted texture for depth */}
                <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-3024" />
                <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-[#EA580C] shadow-[0_0_40px_rgba(234,88,12,0.45)]" data-id="element-3025">
                  <Play size={26} className="text-white translate-x-0.5" fill="currentColor" data-id="element-3026" />
                </div>
                <p className="relative text-sm font-medium text-white/70" data-id="element-3027">
                  Tony's walkthrough lands here
                </p>
              </div>}
          </div>

          {/* Waitlist form / success */}
          <AnimatePresence mode="wait" data-id="element-3028">
            {status === 'done' ? <motion.div key="done" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="relative flex items-center gap-3 rounded-2xl bg-white border border-[#EFE9E1] p-5 overflow-hidden" data-id="element-3029">
                <span className="relative flex items-center justify-center w-9 h-9 shrink-0" data-id="element-3030">
                  {/* Celebratory confetti burst */}
                  {!reduceMotion && <span className="pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-3031">
                      {CONFETTI.map((c, i) => <motion.span key={i} className="absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-[1px]" style={{
                  backgroundColor: c.color
                }} initial={{
                  opacity: 0,
                  scale: 0,
                  x: 0,
                  y: 0
                }} animate={{
                  opacity: [0, 1, 1, 0],
                  scale: [0, 1, 1, 0.6],
                  x: c.x,
                  y: c.y,
                  rotate: c.r
                }} transition={{
                  duration: 0.9,
                  delay: 0.12,
                  ease: [0.16, 1, 0.3, 1]
                }} data-id="element-3032" />)}
                    </span>}
                  <motion.span className="relative z-10 flex items-center justify-center w-9 h-9 rounded-full bg-[#EA580C]" initial={reduceMotion ? {
                opacity: 0
              } : {
                scale: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={reduceMotion ? {
                duration: 0.2
              } : {
                type: 'spring',
                stiffness: 500,
                damping: 18,
                delay: 0.05
              }} data-id="element-3033">
                    <motion.span initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 600,
                  damping: 20,
                  delay: 0.18
                }} data-id="element-3034">
                      <Check size={18} className="text-white" strokeWidth={3} data-id="element-3035" />
                    </motion.span>
                  </motion.span>
                </span>
                <div data-id="element-3036">
                  <p className="font-bold text-[#2B211A]" data-id="element-3037">
                    You're on the list.
                  </p>
                  <p className="text-sm text-[#6B5D52]" data-id="element-3038">
                    We'll be in touch the moment your spot opens up.
                  </p>
                </div>
              </motion.div> : <motion.form key="form" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onSubmit={handleSubmit} className="flex flex-col gap-3" data-id="element-3039">
                <div className="flex flex-col sm:flex-row items-stretch gap-2.5" data-id="element-3040">
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className="flex-1 px-4 py-3 rounded-xl bg-white border border-[#E7E0D7] text-[#2B211A] placeholder:text-[#9C8E82] focus:outline-none focus:ring-2 focus:ring-[#EA580C]/30 focus:border-[#EA580C]/40 transition" data-id="element-3041" />
                  <button type="submit" disabled={status === 'loading'} className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold transition-colors whitespace-nowrap disabled:opacity-80" data-id="element-3042">
                    {status === 'loading' ? <>
                        <Loader2 size={16} className="animate-spin" data-id="element-3043" />
                        Joining…
                      </> : 'Join the waitlist'}
                  </button>
                </div>

                <label className="flex items-center gap-2 text-sm text-[#6B5D52] cursor-pointer select-none" data-id="element-3044">
                  <input type="checkbox" checked={keepPosted} onChange={e => setKeepPosted(e.target.checked)} className="w-4 h-4 rounded border-[#D8CEC2] text-[#EA580C] focus:ring-[#EA580C]/30 accent-[#EA580C]" data-id="element-3045" />
                  Keep me posted on product news
                </label>

                <p className="flex items-center gap-1.5 text-xs text-[#9C8E82]" data-id="element-3046">
                  <ShieldCheck size={12} data-id="element-3047" />
                  No spam. We'll only email about your spot.
                </p>
              </motion.form>}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>;
}